'use client';

import { forwardRef, type HTMLAttributes } from 'react';

import { cn } from '../../utils/cn';

import { UzBadge, type UzBadgeProps } from './UzBadge';

/**
 * Semantic status badge with predefined statuses for the app.
 *
 * Extend the `STATUS_MAP` when adding new business statuses. Centralised here
 * so colours stay consistent across leads, itineraries, quotations, payments.
 */

const STATUS_MAP = {
  // Itinerary
  draft: { label: 'Draft', tone: 'secondary' as const, dot: true },
  quoted: { label: 'Quoted', tone: 'default' as const, dot: true },
  confirmed: { label: 'Confirmed', tone: 'success' as const, dot: true },
  cancelled: { label: 'Cancelled', tone: 'destructive' as const, dot: true },

  // Lead lifecycle
  new: { label: 'New', tone: 'secondary' as const, dot: true },
  contacted: { label: 'Contacted', tone: 'default' as const, dot: true },
  qualified: { label: 'Qualified', tone: 'default' as const, dot: true },
  won: { label: 'Won', tone: 'success' as const, dot: true },
  lost: { label: 'Lost', tone: 'destructive' as const, dot: true },

  // Lead temperature
  cold: { label: 'Cold', tone: 'secondary' as const, dot: true },
  warm: { label: 'Warm', tone: 'warning' as const, dot: true },
  hot: { label: 'Hot', tone: 'destructive' as const, dot: true },

  // Payment
  pending: { label: 'Pending', tone: 'warning' as const, dot: true },
  success: { label: 'Paid', tone: 'success' as const, dot: true },
  failed: { label: 'Failed', tone: 'destructive' as const, dot: true },
  refunded: { label: 'Refunded', tone: 'secondary' as const, dot: true },

  // Misc
  active: { label: 'Active', tone: 'success' as const, dot: true },
  inactive: { label: 'Inactive', tone: 'secondary' as const, dot: true },
} as const;

export type UzStatus = keyof typeof STATUS_MAP;

export interface UzStatusBadgeProps extends Omit<UzBadgeProps, 'children' | 'tone' | 'dot'> {
  status: UzStatus;
  /** Override the auto-generated label. */
  label?: string;
}

export const UzStatusBadge = forwardRef<HTMLDivElement, UzStatusBadgeProps>(
  ({ status, label, className, ...rest }, ref) => {
    const config = STATUS_MAP[status];
    return (
      <UzBadge
        ref={ref}
        tone={config.tone}
        dot={config.dot}
        className={cn(className)}
        data-status={status}
        {...(rest as HTMLAttributes<HTMLDivElement>)}
      >
        {label ?? config.label}
      </UzBadge>
    );
  },
);
UzStatusBadge.displayName = 'UzStatusBadge';
